/**
 * Per-grid user preferences (column visibility, order, widths, sort) held in
 * localStorage so a grid keeps its layout WITHIN a session (across page
 * navigations / reloads). Keyed by a stable `persistKey` the grid supplies (e.g. a
 * module key or a form id). Grids WITHOUT a persistKey keep their in-memory-only
 * behaviour (no-op here).
 *
 * SESSION-ONLY (client decision 2026-08-05): a grid's layout is NOT persisted
 * across log-off. Every login clears these keys (clearAllGridPrefs, called in the
 * login flow AND on logout), so each fresh login starts from the FACTORY-DEFAULT
 * layout and the user's in-session customisations are discarded on log-off. (This
 * replaced an earlier per-user server-side persistence attempt.)
 *
 * All access is wrapped in try/catch — a full quota or private-mode localStorage
 * must never break the grid.
 */

export interface GridPrefs {
  /** Hidden column fields. */
  hidden?: string[];
  /** Full column order (fields), including hidden. */
  order?: string[];
  /** User-dragged column widths, by field. */
  widths?: Record<string, number>;
  /** Persisted sort. */
  sort?: { key: string | null; dir: 'asc' | 'desc' | null };
}

const PREFIX = 'alpaca.grid.';
const storageKey = (k: string) => `${PREFIX}${k}`;

/**
 * Clear EVERY grid's saved layout from localStorage. Called in the login flow AND
 * on logout so each fresh login starts from the FACTORY-DEFAULT layout — grid
 * customisations are session-only and reset on log-off (client decision
 * 2026-08-05). Never throws.
 */
export function clearAllGridPrefs(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* localStorage unavailable — nothing to clear */ }
}

export function loadGridPrefs(persistKey?: string): GridPrefs {
  if (!persistKey) return {};
  try {
    const raw = localStorage.getItem(storageKey(persistKey));
    return raw ? (JSON.parse(raw) as GridPrefs) : {};
  } catch {
    return {};
  }
}

export function saveGridPrefs(persistKey: string | undefined, patch: Partial<GridPrefs>): void {
  if (!persistKey) return;
  try {
    const merged: GridPrefs = { ...loadGridPrefs(persistKey), ...patch };
    localStorage.setItem(storageKey(persistKey), JSON.stringify(merged));
  } catch {
    /* quota exceeded / private mode — persistence is best-effort */
  }
}
